import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, Copy, KeyRound, Loader2, CheckCircle2, RefreshCw, ScanLine } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SecurityTab — Profile & Security controls for the current user.
 *
 * Feature 1: TOTP MFA setup / verification / disable flow.
 *   - GET  /api/auth/mfa/status        -> { mfa_enabled: bool }
 *   - POST /api/auth/mfa/setup         -> { secret, otpauth_uri, qr_code (data URI) }
 *   - POST /api/auth/mfa/verify        -> activate MFA with 6-digit code
 *   - POST /api/auth/mfa/disable       -> disable MFA (requires 6-digit code)
 */
export default function SecurityTab({ authToken, user }) {
    const [status, setStatus] = useState({ loading: true, mfa_enabled: false });
    const [setupData, setSetupData] = useState(null);   // { secret, otpauth_uri, qr_code }
    const [otpCode, setOtpCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [showDisable, setShowDisable] = useState(false);
    const [disableCode, setDisableCode] = useState('');

    const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/mfa/status`, authHeaders);
            setStatus({ loading: false, mfa_enabled: !!data.mfa_enabled });
        } catch (e) {
            setStatus({ loading: false, mfa_enabled: false });
        }
    }, [authToken]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/auth/mfa/setup`, {}, authHeaders);
            setSetupData(data);
            setOtpCode('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start MFA setup');
        } finally {
            setBusy(false);
        }
    };

    const verifyAndActivate = async () => {
        if (otpCode.replace(/\D/g, '').length !== 6) {
            toast.error('Enter the 6-digit code from your authenticator app');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/auth/mfa/verify`, { mfa_code: otpCode.replace(/\D/g, '') }, authHeaders);
            toast.success('MFA activated. You will be asked for a code at every login.');
            setSetupData(null);
            setOtpCode('');
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Invalid code');
        } finally {
            setBusy(false);
        }
    };

    const disableMfa = async () => {
        if (disableCode.replace(/\D/g, '').length !== 6) {
            toast.error('Enter the 6-digit code from your authenticator app');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/auth/mfa/disable`, { mfa_code: disableCode.replace(/\D/g, '') }, authHeaders);
            toast.success('MFA disabled.');
            setShowDisable(false);
            setDisableCode('');
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Invalid code');
        } finally {
            setBusy(false);
        }
    };

    const copySecret = () => {
        if (!setupData?.secret) return;
        navigator.clipboard.writeText(setupData.secret);
        toast.success('Secret copied to clipboard');
    };

    if (status.loading) {
        return (
            <Card>
                <CardContent className="py-12 flex justify-center items-center text-slate-500">
                    <Loader2 className="animate-spin mr-2" /> Loading security settings…
                </CardContent>
            </Card>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Header */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <KeyRound className="w-5 h-5 text-teal-600" />
                        Profile &amp; Security
                    </CardTitle>
                    <CardDescription>
                        Signed in as <span className="font-medium">{user?.email}</span> · Role: <Badge variant="outline">{user?.role}</Badge>
                    </CardDescription>
                </CardHeader>
            </Card>

            {/* MFA Status */}
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                {status.mfa_enabled
                                    ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                    : <ShieldAlert className="w-5 h-5 text-amber-600" />
                                }
                                Two-Factor Authentication (MFA)
                            </CardTitle>
                            <CardDescription className="mt-1">
                                {status.mfa_enabled
                                    ? 'A 6-digit code from your authenticator app is required at every sign-in.'
                                    : 'Add a second factor to your account. Recommended for all administrator and consultant roles.'}
                            </CardDescription>
                        </div>
                        <Badge
                            data-testid="mfa-status-badge"
                            className={status.mfa_enabled
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                : 'bg-amber-100 text-amber-800 border-amber-200'}
                        >
                            {status.mfa_enabled ? 'Enabled' : 'Not Enabled'}
                        </Badge>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    {/* ── NOT ENABLED ── */}
                    {!status.mfa_enabled && !setupData && (
                        <div className="space-y-3">
                            <Alert>
                                <ShieldAlert className="h-4 w-4" />
                                <AlertTitle>Recommended for NHS-compliant deployments</AlertTitle>
                                <AlertDescription>
                                    NHS DSPT and Cyber Essentials Plus require multi-factor authentication for administrative accounts.
                                    Setup takes ~1 minute using any standard authenticator app.
                                </AlertDescription>
                            </Alert>
                            <Button
                                onClick={startSetup}
                                disabled={busy}
                                data-testid="mfa-start-setup-btn"
                                className="bg-teal-600 hover:bg-teal-700"
                            >
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
                                Enable MFA
                            </Button>
                        </div>
                    )}

                    {/* ── SETUP IN PROGRESS ── */}
                    {!status.mfa_enabled && setupData && (
                        <div className="space-y-6" data-testid="mfa-setup-panel">
                            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50 space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-slate-700 mb-1">Step 1 · Scan the QR code</div>
                                    <div className="text-xs text-slate-500 mb-3">
                                        Open Google Authenticator, Authy, 1Password or Microsoft Authenticator and scan this code.
                                    </div>
                                    <div className="flex justify-center bg-white p-4 rounded border border-slate-200">
                                        <img
                                            src={setupData.qr_code}
                                            alt="MFA QR code"
                                            className="w-52 h-52"
                                            data-testid="mfa-qr-image"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="text-sm font-medium text-slate-700 mb-1">
                                        Can&apos;t scan? Enter this secret manually:
                                    </div>
                                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded px-3 py-2">
                                        <code
                                            className="text-xs sm:text-sm font-mono break-all flex-1 select-all"
                                            data-testid="mfa-secret-string"
                                        >
                                            {setupData.secret}
                                        </code>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={copySecret}
                                            data-testid="mfa-copy-secret-btn"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="mfa-verify-code" className="text-sm font-medium">
                                    Step 2 · Enter the 6-digit code from your authenticator
                                </Label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Input
                                        id="mfa-verify-code"
                                        value={otpCode}
                                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        placeholder="000 000"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        className="tracking-[0.5em] text-center text-lg font-mono max-w-[200px]"
                                        data-testid="mfa-verify-code-input"
                                    />
                                    <Button
                                        onClick={verifyAndActivate}
                                        disabled={busy || otpCode.length !== 6}
                                        className="bg-teal-600 hover:bg-teal-700"
                                        data-testid="mfa-activate-btn"
                                    >
                                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                        Activate MFA
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={() => { setSetupData(null); setOtpCode(''); }}
                                        data-testid="mfa-cancel-setup-btn"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── ENABLED ── */}
                    {status.mfa_enabled && !showDisable && (
                        <div className="space-y-3">
                            <Alert className="border-emerald-200 bg-emerald-50">
                                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                                <AlertTitle className="text-emerald-800">MFA is active</AlertTitle>
                                <AlertDescription className="text-emerald-800">
                                    You&apos;ll be asked for a 6-digit code every time you sign in.
                                    Keep your authenticator app installed and backed up.
                                </AlertDescription>
                            </Alert>
                            <Button
                                variant="outline"
                                onClick={() => setShowDisable(true)}
                                data-testid="mfa-disable-btn"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Disable MFA
                            </Button>
                        </div>
                    )}

                    {status.mfa_enabled && showDisable && (
                        <div className="space-y-3 border border-red-200 bg-red-50 rounded-lg p-4" data-testid="mfa-disable-panel">
                            <div className="text-sm text-red-800">
                                Enter your current 6-digit code to confirm disabling MFA:
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Input
                                    value={disableCode}
                                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="000 000"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    className="tracking-[0.5em] text-center text-lg font-mono max-w-[200px]"
                                    data-testid="mfa-disable-code-input"
                                />
                                <Button
                                    variant="destructive"
                                    onClick={disableMfa}
                                    disabled={busy || disableCode.length !== 6}
                                    data-testid="mfa-confirm-disable-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    Confirm Disable
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={() => { setShowDisable(false); setDisableCode(''); }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
}
